import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
  Stack,
  Button,
  LinearProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import {
  downloadMarkdown,
  FileItem,
  generateMarkdownDocument,
  ProcessingStatus,
} from "../utils/markdownExportUtils";
import { generatePDFWithProgress } from "../utils/pdfExportUtils";

interface LocationState {
  parentFile: File;
  selectedFiles: FileItem[];
  allFiles: File[];
}

const Export: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { parentFile, selectedFiles, allFiles } =
    (location.state as LocationState) || {};

  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    progress: 0,
    currentFile: "",
    error: null,
  });
  const [markdownContent, setMarkdownContent] = useState<string>("");
  const [pdfProcessing, setPdfProcessing] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);

  // Redirect if no files provided
  useEffect(() => {
    if (!parentFile || !selectedFiles || !allFiles) {
      navigate("/file-select");
    }
  }, [parentFile, selectedFiles, allFiles, navigate]);

  const handleBack = () => {
    navigate("/traverse", {
      state: {
        parentFile,
        allFiles,
      },
    });
  };

  const generateMarkdown = useCallback(async () => {
    if (!selectedFiles || selectedFiles.length === 0) return null;

    setProcessingStatus({
      isProcessing: true,
      progress: 0,
      currentFile: "",
      error: null,
    });

    try {
      // Generate markdown with progress callback
      const markdown = await generateMarkdownDocument(
        parentFile.name,
        selectedFiles,
        allFiles,
        (status) => {
          setProcessingStatus((prev) => ({ ...prev, ...status }));
        }
      );

      setMarkdownContent(markdown);

      setProcessingStatus((prev) => ({
        ...prev,
        isProcessing: false,
        progress: 100,
        currentFile: "Complete!",
      }));

      return markdown;
    } catch (error) {
      setProcessingStatus({
        isProcessing: false,
        progress: 0,
        currentFile: "",
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      });
      return null;
    }
  }, [selectedFiles, parentFile, allFiles]);

  const handleStartExport = useCallback(async () => {
    await generateMarkdown();
  }, [generateMarkdown]);

  const handleDownloadMarkdown = () => {
    if (!markdownContent) return;

    const filename = `${parentFile.name.replace(".html", "")}_export.md`;
    downloadMarkdown(markdownContent, filename);
  };

  const handleExportPDF = async () => {
    let contentToExport = markdownContent;

    // If markdown hasn't been generated yet, generate it first
    if (!contentToExport) {
      const generatedContent = await generateMarkdown();
      if (!generatedContent) return; // Exit if generation failed
      contentToExport = generatedContent;
    }

    setPdfProcessing(true);
    setPdfProgress(0);

    try {
      const filename = `${parentFile.name.replace(".html", "")}_export.pdf`;

      await generatePDFWithProgress(
        contentToExport,
        {
          filename,
          title: parentFile.name.replace(".html", ""),
          author: "Markdown to PDF Exporter",
          subject: "Exported from HTML files",
        },
        (progress) => {
          setPdfProgress(progress);
        }
      );

      setPdfProcessing(false);
      setPdfProgress(100);

      // Reset progress after a short delay
      setTimeout(() => {
        setPdfProgress(0);
      }, 1000);
    } catch (error) {
      setPdfProcessing(false);
      setPdfProgress(0);
      setProcessingStatus({
        ...processingStatus,
        error: error instanceof Error ? error.message : "PDF export failed",
      });
    }
  };

  if (!parentFile || !selectedFiles || !allFiles) {
    return null;
  }

  const isProcessing = processingStatus.isProcessing || pdfProcessing;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Export Preview
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Review the files that will be exported in the order shown below.
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">Pages to Export</Typography>
            <Chip
              label={`${selectedFiles.length} pages`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Stack>

          <Paper
            variant="outlined"
            sx={{ p: 2, maxHeight: 300, overflow: "auto" }}
          >
            <List dense>
              {selectedFiles
                .sort((a, b) => a.order - b.order)
                .map((item, index) => (
                  <React.Fragment key={`${item.file.name}-${index}`}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Chip
                              label={item.order}
                              size="small"
                              color="primary"
                              sx={{ minWidth: 32 }}
                            />
                            <Typography variant="body2">
                              {item.path.join(" > ")}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
            </List>
          </Paper>
        </CardContent>
      </Card>

      {processingStatus.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Error</AlertTitle>
          {processingStatus.error}
        </Alert>
      )}

      {processingStatus.isProcessing && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Processing Files
            </Typography>
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={processingStatus.progress}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {Math.round(processingStatus.progress)}% - Processing:{" "}
                {processingStatus.currentFile}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {pdfProcessing && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Generating PDF
            </Typography>
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={pdfProgress}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {Math.round(pdfProgress)}% - Converting to PDF...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {markdownContent && !processingStatus.isProcessing && !pdfProcessing && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Export Ready
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your files have been processed and are ready for export.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          disabled={isProcessing}
        >
          Back to Selection
        </Button>

        <Button
          variant="contained"
          onClick={handleExportPDF}
          startIcon={<PdfIcon />}
          color="primary"
          disabled={isProcessing}
        >
          Export PDF
        </Button>

        {!markdownContent && !isProcessing && (
          <Button
            variant="outlined"
            onClick={handleStartExport}
            startIcon={<PlayArrowIcon />}
          >
            Generate Markdown
          </Button>
        )}

        {markdownContent && !isProcessing && (
          <Button
            variant="outlined"
            onClick={handleDownloadMarkdown}
            startIcon={<DownloadIcon />}
          >
            Download Markdown
          </Button>
        )}
      </Stack>
    </Container>
  );
};

export default Export;
